/**
 * CopyIconButton — "copy this string to the clipboard" affordance
 * (#616). Lucide `Copy` glyph (overlapping squares) by default;
 * swaps to `Check` for ~1.5s after a successful copy, then back.
 *
 * Two shapes from the same primitive (#1216):
 *   - icon-only (default; the original shape) — when most call sites
 *     surface the underlying value alongside the button (id chip,
 *     code header) so a visible label is redundant.
 *   - icon + label — when the caller passes `label`; renders as a
 *     dark-theme "Copy" / "Copied" chip suited to code-block headers
 *     where the value isn't otherwise echoed. Folds in the Pipeline
 *     Integration drawer's rolled-its-own button so the affordance
 *     stays consistent across the app.
 *
 * Usage:
 *   <CopyIconButton value={runId} title="Copy run id" />
 *   <CopyIconButton value={snippet} label="Copy" />
 *
 * Failure mode: `navigator.clipboard.writeText` rejects (insecure
 * context, denied permission) → button stays in the "Copy" state and
 * the failure is logged at console.warn. Surfacing the error in the
 * UI would over-complicate a deliberately minimal affordance; the
 * operator notices via the missing "Copied" feedback and falls back
 * to manual selection.
 */
import { useState } from 'react'
import { Check, Copy } from 'lucide-react'

export interface CopyIconButtonProps {
  /** The string copied to the clipboard on click. When empty/falsy
   * the button renders nothing — there's nothing useful to copy. */
  value: string
  /** Tooltip text shown on hover. Defaults to "Copy" (and swaps to
   * "Copied!" for the confirmed state regardless of override). Pass
   * something specific when context helps (e.g. "Copy run id"). */
  title?: string
  /** Optional explicit ARIA label. Defaults to mirroring `title` so
   * screen readers and hover tooltips stay aligned. Override when the
   * accessible name needs to be more descriptive than the tooltip
   * (e.g. tooltip "Copy to clipboard" + aria-label "Copy token to
   * clipboard" on a page whose surrounding context isn't conveyed by
   * the icon alone). The copied state still announces "Copied!"
   * regardless of override (#1199). */
  ariaLabel?: string
  /** Optional extra classes for the button element. The component
   * brings its own sizing + hover state; callers can layer spacing /
   * color overrides without re-doing the base. */
  className?: string
  /** Optional visible label rendered next to the icon (#1216). When
   * set, the button reads as a dark-theme "Copy" / "Copied" chip
   * suited to code-block headers; when unset, the original icon-only
   * shape. The copied state swaps the label to "Copied" alongside
   * the Check icon. */
  label?: string
  /** Optional callback fired after a successful clipboard write.
   * Used by call sites that want to record telemetry on copy without
   * re-implementing the success/failure handling. Not invoked when
   * the write rejects. */
  onCopied?: () => void
  /** Optional `data-testid` forwarded to the rendered button. Call
   * sites that had a stable testid on their previous inline button
   * (e.g. existing E2E selectors) pass it through here so the test
   * surface doesn't churn alongside the primitive consolidation. */
  testId?: string
}

/** Hover-flash duration after a successful copy, in ms. Long enough
 * to read the check + tooltip; short enough that the button reverts
 * before the operator wonders if it's stuck. */
const CONFIRM_DURATION_MS = 1500

export function CopyIconButton({
  value,
  title,
  ariaLabel,
  className,
  label,
  onCopied,
  testId,
}: CopyIconButtonProps) {
  const [copied, setCopied] = useState(false)

  if (!value) return null

  async function onClick() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), CONFIRM_DURATION_MS)
      onCopied?.()
    } catch (e) {
      // Insecure context, denied permission, or other browser-side
      // refusal. Keep the button in the un-copied state so the
      // operator notices the absent "Copied" feedback and falls back
      // to manual selection.
      console.warn('[CopyIconButton] clipboard write failed', e)
    }
  }

  const Icon = copied ? Check : Copy
  const hoverTitle = copied ? 'Copied!' : (title ?? 'Copy')
  // When a visible label is set, the copied state swaps to "Copied"
  // (no exclamation; matches the existing Pipeline drawer chip's
  // shape that this consolidates). The accessible name still echoes
  // the hover/title for screen readers regardless of which shape.
  const visibleLabel = label === undefined ? null : copied ? 'Copied' : label
  const accessibleName = copied ? 'Copied!' : (ariaLabel ?? title ?? label ?? 'Copy')

  // Two visual shapes from the same primitive (#1216). Icon-only is
  // the original ambient affordance; icon + label is the dark-theme
  // chip used inside code-block headers where the snippet doesn't
  // echo the value being copied.
  const buttonClass = label === undefined
    ? 'inline-flex items-center justify-center h-5 w-5 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors'
    : 'inline-flex items-center gap-1 rounded border border-[#444c56] bg-[#373e47] px-2 py-0.5 text-[11px] text-[#adbac7] hover:text-white hover:bg-[#444c56] transition-colors'

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      title={hoverTitle}
      aria-label={accessibleName}
      data-testid={testId}
      className={buttonClass + (className ? ' ' + className : '')}
    >
      <Icon className="h-3 w-3" />
      {visibleLabel}
    </button>
  )
}
